import React from 'react';
import Layout from '../../components/layout/Layout';
import Hero from '../../components/sections/Hero';
import Section from '../../components/layout/Section';
import CallToAction from '../../components/sections/CallToAction';
import { teamMembers, stats, partners } from '../../data/mockData';

const AboutPage: React.FC = () => {
  return (
    <Layout>
      <Hero
        title="About CodemyAI"
        subtitle="We're on a mission to help businesses and individuals harness the transformative power of AI through education, implementation, and optimization."
        backgroundType="gradient"
        ctaText="Start Your AI Journey"
        ctaLink="/contact"
      />

      {/* Our Story Section */}
      <Section className="bg-white">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Our Story</h2>
            <div className="prose prose-lg mx-auto text-gray-700 leading-relaxed">
              <p className="mb-6">
              Founded in 2025 by Praneet Srivastav, CodemyAI was born out of a vision to bridge the growing gap
              between the rapid evolution of artificial intelligence and its real-world application across businesses
              of all sizes. With years of experience delivering multi-million-dollar information technology projects,
              Praneet recognized that AI’s transformative potential could only be fully realized if it became accessible—not
              just to tech giants with extensive R&D resources, but to organizations and professionals everywhere.
              </p>
              <p className="mb-6">
              What began as a boutique consultancy assisting local businesses in adopting foundational AI solutions has since
               evolved into a full-service AI empowerment firm. Today, CodemyAI serves a diverse clientele ranging from individual
                professionals to enterprises of all sizes. Our guiding principle remains unchanged: AI should act as a force multiplier
                 for human potential—not a substitute for it.
              </p>
              <p className="mb-6">
              Our approach blends deep technical expertise with a practical, results-driven methodology. We don’t simply implement AI
              systems—we partner with our clients to build internal capabilities through education, coaching, and ongoing support.
              This client-first philosophy has fostered strong retention rates and organic growth driven by trusted referrals
              </p>
              <p>
              As AI continues to reshape industries at an unprecedented pace, CodemyAI remains steadfast in its mission: to demystify
              cutting-edge AI technologies and empower organizations to harness them effectively, responsibly, and sustainably—delivering
              measurable business value every step of the way.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Company Stats Section */}
      <Section className="bg-gray-50">
        <div className="container mx-auto px-4 py-16">
          <h2 className="text-3xl font-bold mb-12 text-center">Our Impact</h2>
          <div className="grid md:grid-cols-5 gap-6 text-center">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                <div className="text-4xl font-bold text-primary mb-2">{stat.value}</div>
                <p className="text-lg">{stat.label}</p>
                {stat.unit && <p className="text-sm text-gray-600">{stat.unit}</p>}
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Our Mission and Values */}
      <Section className="bg-white">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center">Our Mission & Values</h2>
            
            <div className="mb-16">
              <h3 className="text-2xl font-semibold mb-4 text-center">Mission</h3>
              <p className="text-lg text-center mb-8">
                To democratize AI by making advanced technologies accessible, understandable, and valuable 
                for businesses and individuals of all sizes and technical backgrounds.
              </p>
            </div>
            
            <h3 className="text-2xl font-semibold mb-8 text-center">Our Core Values</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-xl font-semibold mb-3">Practical Innovation</h4>
                <p>
                  We bridge the gap between cutting-edge AI research and practical business applications, 
                  focusing on solutions that deliver measurable value.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-xl font-semibold mb-3">Empowering Education</h4>
                <p>
                  We believe in teaching our clients to fish, not just giving them the fish. Knowledge 
                  transfer and skill building are central to our approach.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-xl font-semibold mb-3">Human-Centered AI</h4>
                <p>
                  We develop AI solutions that enhance human capabilities and decision-making, not 
                  replace them, with a focus on ethical implementation.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-xl font-semibold mb-3">Measurable Results</h4>
                <p>
                  We're committed to delivering and demonstrating concrete business outcomes from 
                  every AI initiative we undertake.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-xl font-semibold mb-3">Inclusive Excellence</h4>
                <p>
                  We strive to make AI accessible across all industries, company sizes, and technical 
                  backgrounds, with solutions tailored to each client's unique context.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-xl font-semibold mb-3">Continuous Learning</h4>
                <p>
                  In a rapidly evolving field, we commit to ongoing education for ourselves and our 
                  clients, constantly refining our approaches based on new developments.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Team Section */}
      <Section className="bg-gray-50">
        <div className="container mx-auto px-4 py-16">
          <h2 className="text-3xl font-bold mb-12 text-center">Our Leadership Team</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {teamMembers.map(member => (
              <div key={member.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="h-64 bg-gray-200">
                  {/* Placeholder for member photo */}
                  <div className="w-full h-full flex items-center justify-center text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-24 w-24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-1">{member.name}</h3>
                  <p className="text-primary font-medium mb-3">{member.title}</p>
                  <p className="text-gray-700 mb-4">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Partners and Certifications */}
      <Section className="bg-gray-50">
        <div className="container mx-auto px-4 py-16">
          <h2 className="text-3xl font-bold mb-12 text-center">Our Partners & Certifications</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {partners.map((partner, index) => (
              <div key={index} className="bg-white p-8 rounded-lg shadow-md flex flex-col items-center">
                <div className="h-16 w-full flex items-center justify-center mb-4">
                  {/* Placeholder for partner logo */}
                  <div className="text-gray-400 font-bold text-2xl">{partner.name}</div>
                </div>
                <p className="text-center text-primary font-medium">{partner.tier}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>


      {/* Locations Section */}
      <Section className="bg-gray-50">
        <div className="container mx-auto px-4 py-16">
          <h2 className="text-3xl font-bold mb-12 text-center">Our Locations</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4">London Office</h3>
              <p className="mb-2">31 Giles Drive</p>
              <p className="mb-2">London</p>
              <p className="mb-4">United Kingdom</p>
              <p className="text-primary font-medium">Main Office</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4">India Office</h3>
              <p className="mb-2">19, Pride Complex</p>
              <p className="mb-2">Bangalore</p>
              <p className="mb-4">India</p>
              <p className="text-primary font-medium">India Operations</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4">Dublin Office</h3>
              <p className="mb-2">2, Stratten Walk</p>
              <p className="mb-2">Dublin</p>
              <p className="mb-4">Ireland</p>
              <p className="text-primary font-medium">European Operations</p>
            </div>
          </div>
        </div>
      </Section>

      {/* Call to Action */}
      <CallToAction
        title="Ready to partner with us today?"
        description="Contact our team today to discuss how we can help you leverage AI to transform your business or career."
        ctaText="Get in Touch"
        ctaLink="/contact"
      />
    </Layout>
  );
};

export default AboutPage;